import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { apiClient } from '../services/apiClient'
import Navigation from '../components/Navigation'

interface LocationFormData {
  companyName: string
  locationIdentifier: string
  address: string
  description: string
  websiteUrl: string
}

interface LocationFormErrors {
  companyName?: string
  locationIdentifier?: string
  address?: string
  websiteUrl?: string
}

interface UserLocation {
  locationGuid: string
  name: string
  address: string
  description?: string
  websiteUrl?: string
}

const LocationPage: React.FC = () => {
  const { user } = useAuth()
  const [formData, setFormData] = useState<LocationFormData>({
    companyName: '',
    locationIdentifier: '',
    address: '',
    description: '',
    websiteUrl: ''
  })
  const [formErrors, setFormErrors] = useState<LocationFormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [locations, setLocations] = useState<UserLocation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [registrationError, setRegistrationError] = useState<string | null>(null)

  // Load locations on component mount
  useEffect(() => {
    loadLocations()
  }, [])

  const loadLocations = async () => {
    try {
      setLoading(true)
      setError(null)
      const userLocations = await apiClient.getUserLocations()
      setLocations(userLocations)
    } catch (err) {
      console.error('Failed to load locations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load locations')
    } finally {
      setLoading(false)
    }
  }

  const validateForm = (): boolean => {
    const errors: LocationFormErrors = {}

    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required'
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required'
    }

    if (!formData.websiteUrl.trim()) {
      errors.websiteUrl = 'Website URL is required'
    } else {
      try {
        new URL(formData.websiteUrl)
      } catch {
        errors.websiteUrl = 'Please enter a valid URL'
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setSubmitting(true)
    setRegistrationError(null)
    
    try {
      await apiClient.registerLocation({
        companyName: formData.companyName,
        locationIdentifier: formData.locationIdentifier || undefined,
        address: formData.address,
        description: formData.description || undefined,
        websiteUrl: formData.websiteUrl
      })

      // Reset form
      setFormData({
        companyName: '',
        locationIdentifier: '',
        address: '',
        description: '',
        websiteUrl: ''
      })
      setShowForm(false)
      setSuccessMessage('Location registered successfully! You should receive email confirmations shortly.')
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000)
      
      // Reload locations
      await loadLocations()
    } catch (err) {
      console.error('Registration failed:', err)
      setRegistrationError(err instanceof Error ? err.message : 'Registration failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleInputChange = (field: keyof LocationFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing (only for fields that can have errors)
    if (field in formErrors && formErrors[field as keyof LocationFormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  // This shouldn't happen due to route protection, but adding safety check
  if (!user) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Redirecting...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-white mb-4">
            Location <span className="text-firedup-orange">Registration</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Success Message */}
          {successMessage && (
            <div className="lg:col-span-3 mb-6">
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 flex items-center gap-3">
                <div className="text-green-400 text-xl">✅</div>
                <div className="text-green-200">{successMessage}</div>
              </div>
            </div>
          )}

          {/* Your Registered Locations - Now Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
              <h2 className="text-2xl font-bold text-white mb-6">Your Locations</h2>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="text-firedup-orange text-lg">Loading locations...</div>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">⚠️</div>
                  <div className="text-red-400 text-lg mb-2">Failed to load locations</div>
                  <p className="text-gray-400 text-sm mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="btn-secondary text-sm"
                  >
                    Retry
                  </button>
                </div>
              ) : locations.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">🏗️</div>
                  <div className="text-gray-400 text-lg mb-2">No locations registered yet</div>
                  <p className="text-sm text-gray-500 mb-6">
                    Register your first location using the form on the right to get started!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {locations.map((location) => (
                    <div
                      key={location.locationGuid}
                      className="bg-firedup-dark/50 p-4 rounded-lg border border-firedup-light-gray"
                    >
                      <div className="mb-3">
                        <h3 className="font-bold text-firedup-orange text-lg leading-tight mb-2">
                          {location.name}
                        </h3>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs font-mono">
                            {location.locationGuid}
                          </span>
                          <button
                            onClick={(event) => {
                              navigator.clipboard.writeText(location.locationGuid)
                              // Optional: Show a brief success indicator
                              const button = event.target as HTMLButtonElement
                              const originalText = button.textContent
                              button.textContent = '✓'
                              button.classList.add('text-green-400')
                              setTimeout(() => {
                                button.textContent = originalText
                                button.classList.remove('text-green-400')
                              }, 1000)
                            }}
                            className="text-gray-500 hover:text-firedup-orange transition-colors text-xs px-1 py-0.5 rounded hover:bg-firedup-light-gray/20"
                            title="Copy Location ID"
                          >
                            📋
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-gray-300 text-sm mb-2">
                        📍 {location.address}
                      </p>
                      
                      {location.description && (
                        <p className="text-gray-400 text-xs mb-2">
                          {location.description}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-4">
                          {location.websiteUrl && (
                            <a 
                              href={location.websiteUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-firedup-orange hover:underline"
                            >
                              🌐 Website
                            </a>
                          )}
                          <a 
                            href={`https://display.firedupgames.com/?locationId=${location.locationGuid}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-firedup-orange hover:underline"
                          >
                            📺 Leaderboard
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Registration Form - Now Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
              <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-white mb-6">Register New Location</h2>
                {!showForm && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="btn-primary text-sm"
                  >
                    + Add New
                  </button>
                )}
              </div>

              {showForm ? (
                <div>
                  {/* Registration Error */}
                  {registrationError && (
                    <div className="mb-6 bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
                      <div className="text-red-400 text-xl">❌</div>
                      <div className="text-red-200">{registrationError}</div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        value={formData.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        maxLength={30}
                        className={`w-full px-3 py-2 bg-firedup-dark border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-firedup-orange transition-colors text-sm ${
                          formErrors.companyName ? 'border-red-500' : 'border-firedup-light-gray'
                        }`}
                        placeholder="Enter company name"
                      />
                      {formErrors.companyName && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.companyName}</p>
                      )}
                      <p className="text-gray-400 text-xs mt-1">
                        {formData.companyName.length}/30 characters
                      </p>
                    </div>

                    <div>
                      <label htmlFor="locationIdentifier" className="block text-sm font-medium text-gray-300 mb-2">
                        Location Identifier
                      </label>
                      <input
                        type="text"
                        id="locationIdentifier"
                        value={formData.locationIdentifier}
                        onChange={(e) => handleInputChange('locationIdentifier', e.target.value)}
                        maxLength={26}
                        className={`w-full px-3 py-2 bg-firedup-dark border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-firedup-orange transition-colors text-sm ${
                          formErrors.locationIdentifier ? 'border-red-500' : 'border-firedup-light-gray'
                        }`}
                        placeholder="e.g., DOWNTOWN-01"
                      />
                      {formErrors.locationIdentifier && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.locationIdentifier}</p>
                      )}
                      <p className="text-gray-400 text-xs mt-1">
                        {formData.locationIdentifier.length}/26 characters (optional)
                      </p>
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-2">
                        Location Address *
                      </label>
                      <textarea
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows={3}
                        className={`w-full px-3 py-2 bg-firedup-dark border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-firedup-orange transition-colors resize-none text-sm ${
                          formErrors.address ? 'border-red-500' : 'border-firedup-light-gray'
                        }`}
                        placeholder="Enter full address including city, state, and postal code"
                      />
                      {formErrors.address && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                        Description
                      </label>
                      <textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 bg-firedup-dark border border-firedup-light-gray rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-firedup-orange transition-colors resize-none text-sm"
                        placeholder="Optional description of your gaming location"
                      />
                    </div>

                    <div>
                      <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-300 mb-2">
                        Website URL *
                      </label>
                      <input
                        type="url"
                        id="websiteUrl"
                        value={formData.websiteUrl}
                        onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                        className={`w-full px-3 py-2 bg-firedup-dark border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-firedup-orange transition-colors text-sm ${
                          formErrors.websiteUrl ? 'border-red-500' : 'border-firedup-light-gray'
                        }`}
                        placeholder="https://yourwebsite.com"
                      />
                      {formErrors.websiteUrl && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.websiteUrl}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary w-full text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? 'Registering...' : 'Register Location'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false)
                          setFormData({
                            companyName: '',
                            locationIdentifier: '',
                            address: '',
                            description: '',
                            websiteUrl: ''
                          })
                          setFormErrors({})
                          setRegistrationError(null)
                        }}
                        className="btn-secondary w-full text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">🏢</div>
                  <p className="text-gray-300 text-sm mb-4">
                    Add your location to our platform and start tracking scores.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LocationPage
